// RFC 7807 ProblemDetails — the typed shape every backend error body takes.
//
// The VoiceDojo backend's error path (`ProblemResults.*` /
// `ValidationExceptionMiddleware`) emits KEYS, not resolved text: the active
// culture lives only in the frontend (localStorage `voicedojo:culture`) and is
// not sent to the backend, so server-resolved error text would be
// wrong-language. The body carries dotted label keys plus their interpolation
// args; the FE resolves them via `t(key, args)` — the same convention the SSE
// error frames already use.
//
// Wire shape:
//   • `title` / `detail` — a dotted label KEY (e.g. `"Recordings.Validations.UserMismatch"`).
//   • `args` — positional interpolation array for `title`/`detail`
//     (validation strings use `{0}`/`{1}` placeholders).
//   • `errors` — field → array of `{ key, args }` (one entry per field error).
//   • plus arbitrary extension members for domain data (the practice-quota
//     `{ limit, remaining, scope }` snapshot, the admin `currentBalance`, …).
//
// `@voicedojo/backend-types` does not (yet) expose a generated ProblemDetails,
// so this hand-written shape is the canonical FE type. The index signature
// keeps domain extensions reachable as `problem['limit']` without losing the
// named RFC-7807 members.

/** One field-level error on the wire: a label key + its interpolation args. */
export interface FieldError {
  key: string;
  args?: unknown[];
}

export interface ProblemDetails {
  type?: string;
  /** A dotted label KEY — localize via `t(title, argsToParams(args))`. */
  title?: string;
  status?: number;
  /** A dotted label KEY — localize via `t(detail, argsToParams(args))`. */
  detail?: string;
  instance?: string;
  /** Positional interpolation args for `title`/`detail`. */
  args?: unknown[];
  /** Field → `{ key, args }[]`. Each entry is localized via `t(key, …)`. */
  errors?: Record<string, FieldError[]>;
  /** Domain extension members (limit/remaining/scope, currentBalance, …). */
  [extension: string]: unknown;
}

/** Narrows an unknown parsed body to a `ProblemDetails` when it looks like one. */
export function isProblemDetails(body: unknown): body is ProblemDetails {
  return typeof body === 'object' && body !== null;
}

/**
 * Maps a backend positional `args` array to the `{ '0': …, '1': … }` params
 * object `t()` interpolates into `{0}`/`{1}` placeholders. Returns `undefined`
 * for an empty/absent array so `t()` skips interpolation.
 */
export function argsToParams(
  args: unknown[] | undefined,
): Record<string, string | number> | undefined {
  if (!args || args.length === 0) return undefined;
  const params: Record<string, string | number> = {};
  args.forEach((value, index) => {
    params[String(index)] =
      typeof value === 'number' ? value : String(value ?? '');
  });
  return params;
}
